import {ConstraintsBuilderInterface} from './constraints-builder-interface';
import {StackItemMarker} from './stack-item-marker';
import {isPlayerOnDoublePosition} from 'helpers/player-helper';
import {StackItemInterface} from '../interfaces';
import RuleBuilder from 'services/simplex/rule-builder';
import {addRoleSuffix, getSimplexKeyUnitName, getSimplexUnitName} from 'helpers/simplex-helper';

interface Dependencies {
    readonly checkIfPlayerOnDoublePosition: boolean;
    readonly modes: string[];
    readonly players: object;
}

let dependencies: Dependencies = {
    checkIfPlayerOnDoublePosition: false,
    modes: [],
    players: {},
};

class SimplexConstraintsBuilder implements ConstraintsBuilderInterface {
    private dependencies: Dependencies = dependencies;
    private simplexUnitId: string = '';

    public setDependencies(dependencies: Dependencies): void {
        this.dependencies = dependencies;
    }
    public createStackItemConstraints(stackItem: StackItemInterface): object[] {
        this.simplexUnitId = this.createSimplexKeyUnitId(stackItem);

        const primaryGroupConstraints = this.createPrimaryGroupConstraints(stackItem);

        if (isEmpty(primaryGroupConstraints)) {
            return [];
        }

        let secondaryGroupConstraints: object[] = [];

        if (stackItem.isSecondaryGroupUsed()) {
            secondaryGroupConstraints = this.createSecondaryGroupConstraints(stackItem);
            if (isEmpty(secondaryGroupConstraints)) {
                return [];
            }
        }

        return [...primaryGroupConstraints, ...secondaryGroupConstraints];
    }
    public createStackKeyUnitsConstraint(stackItems: StackItemInterface[], atLeast: number): object {
        const ruleBuilder = RuleBuilder.fromAtLeast(atLeast);

        stackItems.map((stackItem) => {
            const simplexUnitId = this.createSimplexKeyUnitId(stackItem);
            ruleBuilder.addVar(simplexUnitId, 1);
        });

        if (ruleBuilder.isRuleCreatable()) {
            return ruleBuilder.createRule();
        }

        return {};
    }
    public createLockedKeyUnitConstraint(stackItem: StackItemInterface): object {
        const ruleBuilder = RuleBuilder.fromExactly(1);

        const simplexUnitId = this.createSimplexKeyUnitId(stackItem);
        ruleBuilder.addVar(simplexUnitId, 1);

        return ruleBuilder.createRule();
    }
    public createObjective(stackItems: StackItemInterface[], constant: number): object {
        const objective = {};
        stackItems.map((stackItem) => {
            const simplexUnitId = this.createSimplexKeyUnitId(stackItem);
            objective[simplexUnitId] = constant;
        });
        return objective;
    }
    private createSimplexKeyUnitId(stackItem: StackItemInterface): string {
        return StackItemMarker.setSuffix(getSimplexKeyUnitName(stackItem.getId()));
    }
    private createPrimaryGroupConstraints(stackItem: StackItemInterface): object[] {
        return this.createGroupConstraints(
            stackItem.getPlayerIdsInPrimaryGroup(),
            stackItem.getMinPlayersInPrimaryGroup(),
            stackItem.getMaxPlayersInPrimaryGroup(),
        );
    }
    private createSecondaryGroupConstraints(stackItem: StackItemInterface): object[] {
        return this.createGroupConstraints(
            stackItem.getPlayerIdsInSecondaryGroup(),
            stackItem.getMinPlayersInSecondaryGroup(),
            stackItem.getMaxPlayersInSecondaryGroup(),
        );
    }
    private createGroupConstraints(playerIds: any[], min: number, max: number): object[] {
        const row = this.createRow(playerIds);
        const simplexUnitIds = Object.keys(row);

        if (simplexUnitIds.length < min) {
            return [];
        }

        const atLeastConstant = min > 0 ? -min : 0;
        const atLeastRuleBuilder = RuleBuilder.fromAtLeast(0);
        atLeastRuleBuilder.addVars(row);
        atLeastRuleBuilder.addVar(this.simplexUnitId, atLeastConstant);

        const atMostRuleBuilder = RuleBuilder.fromAtMost(simplexUnitIds.length);
        atMostRuleBuilder.addVars(row);
        atMostRuleBuilder.addVar(this.simplexUnitId, simplexUnitIds.length - max);

        return [atLeastRuleBuilder.createRule(), atMostRuleBuilder.createRule()];
    }
    private createRow(playerIds: any[]): object {
        const {players, modes} = this.dependencies;
        const row = {};
        playerIds.map((pId) => {
            if (!players.hasOwnProperty(pId)) {
                return;
            }

            if (this.isPlayerOnDoublePositionByPlayerId(pId)) {
                players[pId].position.map((pos) => {
                    row[getSimplexUnitName([pId, pos])] = 1;
                });
                return;
            }

            const simplexUnitName = getSimplexUnitName(pId);

            if (isEmpty(modes)) {
                row[simplexUnitName] = 1;
            } else {
                modes.map((mode) => {
                    const simplexModeUnitName = addRoleSuffix({
                        prefix: simplexUnitName,
                        role: mode,
                    });
                    row[simplexModeUnitName] = 1;
                });
            }
        });
        return row;
    }
    private isPlayerOnDoublePositionByPlayerId(playerId: any): boolean {
        const {checkIfPlayerOnDoublePosition, players} = this.dependencies;
        if (!checkIfPlayerOnDoublePosition) {
            return false;
        }
        return players.hasOwnProperty(playerId) && isPlayerOnDoublePosition(players[playerId]);
    }
}

export {SimplexConstraintsBuilder};
